import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import { textCapitalize } from "../../../Helpers/Utils";
import { Skeleton } from "../../../Components/Needed";

const emptyForm = { convenienceFee: "0", gst: "0", platformFee: "0", dop: "0" };

const FIELDS = [
    { key: "convenienceFee", label: "Convenience fee (%)", hint: "0-100", min: 0, max: 100 },
    { key: "gst", label: "GST (%)", hint: "0-100", min: 0, max: 100 },
    { key: "platformFee", label: "Platform fee", hint: "flat amount", min: undefined, max: undefined },
    { key: "dop", label: "DOP", hint: "flat amount", min: undefined, max: undefined },
];

const TaxTab = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(emptyForm);

    const load = useCallback(async () => {
        setLoading(true);
        const response = await Handler({ method: "GET", url: "/staff/business/site/tax/view" });
        if (response?.success) {
            const data = response?.data?.data || {};
            setForm({
                convenienceFee: String(data.convenienceFee ?? 0),
                gst: String(data.gst ?? 0),
                platformFee: String(data.platformFee ?? 0),
                dop: String(data.dop ?? 0),
            });
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    const onSave = async () => {
        for (const field of FIELDS) {
            const value = Number(form[field.key]);
            if (Number.isNaN(value)) {
                toast.error(`${field.label} must be a number`);
                return;
            }
            if (field.min !== undefined && (value < field.min || value > field.max)) {
                toast.error(`${field.label} must be between ${field.min} and ${field.max}`);
                return;
            }
        }
        setSaving(true);
        const response = await Handler({
            method: "patch",
            url: "/staff/business/site/tax/update",
            data: {
                convenienceFee: Number(form.convenienceFee),
                gst: Number(form.gst),
                platformFee: Number(form.platformFee),
                dop: Number(form.dop),
            },
        });
        if (response?.success) {
            toast.success(response?.data?.message || "Tax settings updated");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update tax settings"));
        }
        setSaving(false);
    };

    if (loading) {
        return (
            <div className="settings-card">
                <Skeleton height={24} width="40%" className="mb-3" />
                <Skeleton height={38} className="mb-3" />
                <Skeleton height={38} className="mb-3" />
            </div>
        );
    }

    return (
        <div className="settings-card">
            <h5 className="fw-semibold mb-3">Tax &amp; Fees</h5>
            <p className="text-muted small mb-4">Applied to every booking at checkout - convenience fee and GST are percentages, platform fee and DOP are flat amounts.</p>

            <div className="row g-4">
                {FIELDS.map((field) => (
                    <div className="col-12 col-md-6" key={field.key}>
                        <label className="form-label" htmlFor={`tax-${field.key}`}>{field.label}</label>
                        <input
                            id={`tax-${field.key}`}
                            type="number"
                            step="0.01"
                            name={field.key}
                            className="form-control"
                            value={form[field.key]}
                            onChange={handleChange}
                        />
                        <div className="form-text">{field.hint}</div>
                    </div>
                ))}
            </div>

            <div className="settings-card-actions">
                <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving}>
                    {saving ? "Saving..." : "Save changes"}
                </button>
            </div>
        </div>
    );
};

export default TaxTab;
